import { equals, find } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../../../../../ErrorHandling';
import { getUpdatedSearchParameters, getUpdatedSortParameters } from '../../../../../ListFilter';
import { SearchParameter, SearchParameters } from '../../../../../ListFilter/Types';
import { Link } from '../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../Translations';
import { BulkInvitationsResource } from '../BulkActions/EditInvitations/Store/Types';
import InvitationsOptionsStore from './Options';
import { State as InvitationsOptionsState } from './Options/Types';
import {
  InvitationResource,
  InvitationsResource,
  Ready,
  SortableColumn,
  State,
  initializing,
  loading,
  loadingMore,
  ready,
  sorting,
  updatingQuery,
  updatingSearchParameters,
} from './Types';
import { searchParametersOrDefault } from './Utils';

class InvitationsStore {
  @observable
  state: State = initializing(searchParametersOrDefault());
  readonly learningCollectionId: number;

  readonly optionsStore: InvitationsOptionsStore;

  constructor(learningCollectionId: number) {
    makeObservable(this);
    this.optionsStore = new InvitationsOptionsStore(this);
    this.learningCollectionId = learningCollectionId;
  }

  updatedSearchParameters = (searchParameter: SearchParameter, state: Ready) =>
    getUpdatedSearchParameters(
      {
        newSearchParameter: searchParameter,
        searchParameters: state.searchParameters,
      },
      'invitations_filter',
    );

  @action
  loading = (page: number) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'updating-search-parameters':
      case 'sorting':
      case 'ready':
        this.state = loading(this.state, page);
        break;
      case 'loading-more':
      case 'updating-query':
      case 'loading':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingMore = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loadingMore(this.state, page);
        break;
      case 'initializing':
      case 'updating-search-parameters':
      case 'sorting':
      case 'updating-query':
      case 'loading':
      case 'loading-more':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (resource: InvitationsResource) => {
    switch (this.state.kind) {
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
        this.state = ready(this.state, resource);
        break;
      case 'sorting':
      case 'initializing':
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingQuery = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingQuery(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingSearchParameters = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingSearchParameters(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  sorting = (sortedColumn: SortableColumn) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = getUpdatedSortParameters(
          { searchParameters: this.state.searchParameters, sortedColumn },
          'invitations_filter',
        );
        this.state = sorting(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        this.state = error(msg);
        break;
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  pageTo = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loading(this.state, page);
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  replaceWithUpdatedResources = (
    state: Ready,
    updatedResources: InvitationResource[],
  ): InvitationsResource => {
    return {
      ...state.invitationsResource,
      payload: {
        ...state.invitationsResource.payload,
        invitations: updatedResources,
      },
    };
  };

  replaceUpdatedInvitation = (
    state: Ready,
    updatedInvitation: InvitationResource,
  ): InvitationResource[] =>
    state.invitationsResource.payload.invitations.map((r) =>
      when(equals(r.payload.id, updatedInvitation.payload.id), updatedInvitation).getOrElseValue(r),
    );

  replaceUpdatedInvitations = (
    state: Ready,
    resource: BulkInvitationsResource,
  ): InvitationResource[] =>
    state.invitationsResource.payload.invitations.map((originalResource) =>
      find(
        (updatedResource: InvitationResource): boolean =>
          originalResource.payload.id === updatedResource.payload.id,
      )(resource.payload.updatedInvitations).getOrElseValue(originalResource),
    );

  @action
  updateInvitationInInvitations = (resource: InvitationResource) => {
    switch (this.state.kind) {
      case 'ready':
        const updatedInvitations = this.replaceUpdatedInvitation(this.state, resource);
        this.state = ready(
          this.state,
          this.replaceWithUpdatedResources(this.state, updatedInvitations),
        );
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateInvitations = (resource: BulkInvitationsResource) => {
    switch (this.state.kind) {
      case 'ready':
        const updatedInvitations = this.replaceUpdatedInvitations(this.state, resource);
        this.state = ready(
          this.state,
          this.replaceWithUpdatedResources(this.state, updatedInvitations),
        );
        break;
      case 'loading-more':
      case 'loading':
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get searchParameters(): SearchParameters {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading':
      case 'loading-more':
      case 'initializing':
        return this.state.searchParameters;
      case 'error':
        return [];
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'ready':
        return this.state.invitationsResource.links;
      case 'loading':
      case 'loading-more':
      case 'initializing':
      case 'error':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        return [];
    }
  }

  @computed
  get displayedCount(): number {
    switch (this.state.kind) {
      case 'ready':
        return this.state.invitationsResource.payload.invitations.length;
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'error':
        return 0;
    }
  }

  @computed
  get optionsState(): InvitationsOptionsState {
    switch (this.state.kind) {
      case 'ready':
        return { kind: 'loading', links: this.links };
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'error':
        return this.optionsStore._state;
    }
  }
}

export default InvitationsStore;
